import { onUnmounted, Ref, watch } from '@vue/composition-api'
import * as Comlink from 'comlink'
import type { ExposeIframeOptions } from '../types/iframe'

type ReleasableIframeEndpoint = {
  endpoint: Comlink.Endpoint
  release: () => void
}

type EndpointListenerOptions = Parameters<Comlink.Endpoint['addEventListener']>[2]

type ScopedListener = {
  listener: EventListener
  options?: EndpointListenerOptions
}

function createScopedIframeEndpoint(iframeWindow: Window, targetOrigin: string): ReleasableIframeEndpoint {
  const baseEndpoint = Comlink.windowEndpoint(iframeWindow, window, targetOrigin)
  const listeners = new Map<EventListenerOrEventListenerObject, ScopedListener>()

  const endpoint: Comlink.Endpoint = {
    postMessage: baseEndpoint.postMessage,
    start: baseEndpoint.start,
    addEventListener(type, listener, options) {
      if (type !== 'message') return

      const sourceListener: EventListener = (event) => {
        const messageEvent = event as MessageEvent
        if (messageEvent.source !== iframeWindow) return

        if (typeof listener === 'function') {
          listener(messageEvent)
        } else {
          listener.handleEvent(messageEvent)
        }
      }

      listeners.set(listener, { listener: sourceListener, options })
      baseEndpoint.addEventListener(type, sourceListener, options)
    },
    removeEventListener(type, listener) {
      if (type !== 'message') return

      const scopedListener = listeners.get(listener)
      if (!scopedListener) return

      baseEndpoint.removeEventListener(type, scopedListener.listener, scopedListener.options)
      listeners.delete(listener)
    },
  }

  return {
    endpoint,
    release: () => {
      listeners.forEach(({ listener, options }) => {
        baseEndpoint.removeEventListener('message', listener, options)
      })
      listeners.clear()
    },
  }
}

/**
 * 暴露对象给 iframe 子应用调用
 * 用于父应用将 API 暴露给 iframe 子应用
 *
 * 注意：此功能需要安装 comlink 依赖
 * yarn add comlink
 * yarn add -D @types/comlink
 *
 * @param target - 要暴露的对象
 * @param iframeRef - iframe 元素的 ref
 * @param options - 配置选项
 *
 * @example
 * ```ts
 * const parentApi = {
 *   async getData() {
 *     return { data: 'hello' }
 *   }
 * }
 * const iframeRef = ref<HTMLIFrameElement | null>(null)
 * useExposeToChildIframe(parentApi, iframeRef)
 * ```
 */
export function useExposeToChildIframe<T extends Record<string, unknown>>(
  target: T,
  iframeRef: Ref<HTMLIFrameElement | null>,
  options?: ExposeIframeOptions
) {
  const targetOrigin = options?.targetOrigin || '*'
  let releaseEndpoint: (() => void) | undefined

  const stopWatch = watch(
    () => iframeRef.value,
    (iframe) => {
      releaseEndpoint?.()
      releaseEndpoint = undefined

      if (iframe?.contentWindow) {
        const { endpoint, release } = createScopedIframeEndpoint(iframe.contentWindow, targetOrigin)
        releaseEndpoint = release
        Comlink.expose(target, endpoint)
        console.debug('[useExposeToChildIframe] API exposed to child iframe')
      }
    },
    { immediate: true }
  )

  onUnmounted(() => {
    stopWatch()
    releaseEndpoint?.()
    releaseEndpoint = undefined
  })

  return {
    exposed: target,
  }
}
